import React, { useState } from 'react';
import { Table, Spinner, Alert } from 'react-bootstrap';
import './TestKitchen.css';
import { FaPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AddRecommendedPartnerModal from './AddRecommendedPartnerModal';
import {
  useGetRecommendedPartnersAdminQuery,
  useAddRecommendedPartnerMutation,
  useUpdateRecommendedPartnerMutation,
  useDeleteRecommendedPartnerMutation,
  useToggleRecommendedPartnerStatusMutation,
} from '../Services/SecondaryApi';

const RecommendedPartners = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const { data: partners = [], isLoading, error } = useGetRecommendedPartnersAdminQuery();
  const [addPartner] = useAddRecommendedPartnerMutation();
  const [updatePartner] = useUpdateRecommendedPartnerMutation();
  const [deletePartner] = useDeleteRecommendedPartnerMutation();
  const [toggleStatus] = useToggleRecommendedPartnerStatusMutation();

  const sorted = [...partners].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
  );

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete ${item.name}? This removes it from the cafe portal.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await deletePartner(item.id).unwrap();
        Swal.fire('Deleted!', 'The partner has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error!', 'Failed to delete the partner.', 'error');
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      await toggleStatus(item.id).unwrap();
    } catch (err) {
      Swal.fire('Error!', 'Failed to update status.', 'error');
    }
  };

  // Throws on failure so the modal can show the server's field errors
  const handleSave = async (formData) => {
    if (editingItem) {
      await updatePartner({ id: editingItem.id, formData }).unwrap();
    } else {
      await addPartner(formData).unwrap();
    }
    Swal.fire('Success!', `Partner ${editingItem ? 'updated' : 'added'} successfully.`, 'success');
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        Failed to load recommended partners. Please try again later.
      </Alert>
    );
  }

  return (
    <div className="container-fluid py-4">
      <h3>Recommended partners</h3>
      <p className="text-muted">
        The list cafe owners see under Recommended. Only partners switched on are shown, in this order.
      </p>

      <div className="table-wrapper-div">
        <div className="sub-haed-table">
          <div className="d-flex justify-content-end">
            <div className="add-div">
              <button className="primary-btn" onClick={handleAdd}>
                <FaPlus className="me-1" /> Add partner
              </button>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <Table className="table-common">
            <thead>
              <tr>
                <th>Order</th>
                <th className="text-center">Logo</th>
                <th>Name</th>
                <th>Category</th>
                <th className="text-center">Recommended</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length > 0 ? (
                sorted.map((item) => (
                  <tr key={item.id}>
                    <td>{item.sort_order}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center">
                        {item.presigned_logo_url ? (
                          <img
                            src={item.presigned_logo_url}
                            alt={item.name}
                            style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px' }}
                          />
                        ) : (
                          <div style={{ width: '40px', height: '40px', borderRadius: '4px', backgroundColor: '#f5f5f5' }} />
                        )}
                      </div>
                    </td>
                    <td>{item.name}</td>
                    <td>{item.category}</td>
                    <td className="text-center">
                      <label className="switch mb-0">
                        <input
                          type="checkbox"
                          checked={!!item.is_recommended}
                          onChange={() => handleToggle(item)}
                          aria-label={`Recommend ${item.name}`}
                        />
                        <span className="slider round"></span>
                      </label>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center">
                        <button className="btn-outline me-2" onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        <button className="btn-outline" onClick={() => handleDelete(item)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No partners yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <AddRecommendedPartnerModal
        show={showModal}
        onHide={() => setShowModal(false)}
        item={editingItem}
        onSave={handleSave}
      />
    </div>
  );
};

export default RecommendedPartners;
